import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Calculator, 
  Users, 
  Loader2, 
  Save, 
  FileSpreadsheet,
  FileText,
  Umbrella,
  Gift,
  Info,
  Building2,
  Plus,
  Trash2,
  Printer,
  Edit,
  Package
} from 'lucide-react';

// ==========================================
// TABELAS E CONSTANTES SINPRO/SP 2026
// ==========================================

// Tabela INSS 2026 com parcela a deduzir (Fonte: INSS/Receita Federal)
const INSS_TABLE_2026 = [
  { min: 0, max: 1621.00, rate: 0.075, deduction: 0 },
  { min: 1621.01, max: 2902.84, rate: 0.09, deduction: 24.32 },
  { min: 2902.85, max: 4354.27, rate: 0.12, deduction: 111.40 },
  { min: 4354.28, max: 8475.55, rate: 0.14, deduction: 198.49 },
];

// Tabela IRRF 2026 (Fonte: Receita Federal)
const IRRF_TABLE_2026 = [
  { min: 0, max: 2428.80, rate: 0, deduction: 0 },
  { min: 2428.81, max: 2826.65, rate: 0.075, deduction: 182.16 },
  { min: 2826.66, max: 3751.05, rate: 0.15, deduction: 394.16 },
  { min: 3751.06, max: 4664.68, rate: 0.225, deduction: 675.49 },
  { min: 4664.69, max: Infinity, rate: 0.275, deduction: 908.73 },
];

// Desconto simplificado IRRF 2026
const DESCONTO_SIMPLIFICADO = 607.20;

// Valores hora-aula SINPRO/SP 2025/2026 (março/2025 a fevereiro/2026) - Piso Salarial
const HORA_AULA_SINPRO = {
  'ed_infantil_fund_5': { valor: 24.35, descricao: 'Ed. Infantil / Fund. até 5º ano (22h)', salarioMensal: 2142.99 },
  'especialista_fund': { valor: 25.41, descricao: 'Especialistas Fund. I e II' },
  'ensino_medio': { valor: 28.22, descricao: 'Ensino Médio' },
  'tecnico_medio': { valor: 26.84, descricao: 'Técnico de Nível Médio' },
  'pre_vestibular': { valor: 39.38, descricao: 'Cursos Pré-Vestibulares' },
};

// Constantes SINPRO/SP
const HORA_ATIVIDADE_PERCENT = 0.05; // 5% do salário base
const DSR_DIVISOR = 6; // 1/6 para DSR
const FGTS_PERCENT = 0.08; // 8% do valor bruto
const SEMANAS_MES = 4.5; // 4,5 semanas no mês

// ==========================================
// FUNÇÕES DE CÁLCULO
// ==========================================

const calcularINSS = (salarioBruto) => {
  for (const faixa of INSS_TABLE_2026) {
    if (salarioBruto >= faixa.min && salarioBruto <= faixa.max) {
      return Math.max(0, (salarioBruto * faixa.rate) - faixa.deduction);
    }
  }
  return Math.max(0, (8475.55 * 0.14) - 198.49);
};

const calcularIRRF = (salarioBruto, inss) => {
  // Base de cálculo = Salário Bruto - INSS - Desconto Simplificado
  const baseCalculo = salarioBruto - inss - DESCONTO_SIMPLIFICADO;
  
  if (baseCalculo <= 0) return 0;
  
  // Calcular IR pela tabela
  let irPelaTabela = 0;
  for (const faixa of IRRF_TABLE_2026) {
    if (baseCalculo >= faixa.min && baseCalculo <= faixa.max) {
      irPelaTabela = (baseCalculo * faixa.rate) - faixa.deduction;
      break;
    }
  }
  irPelaTabela = Math.max(0, irPelaTabela);
  
  // Aplicar Redução 2026 para rendimentos até R$ 7.350
  let reducao = 0;
  if (salarioBruto <= 5000) {
    // Até R$ 5 mil: redução até R$ 312,89 (zera o imposto)
    reducao = Math.min(irPelaTabela, 312.89);
  } else if (salarioBruto <= 7350) {
    // De R$ 5.000,01 a R$ 7.350: Redução = R$ 978,62 - (0,133145 × Salário Bruto)
    reducao = Math.max(0, 978.62 - (0.133145 * salarioBruto));
  }
  // Acima de R$ 7.350,01: sem redução
  
  const irrfFinal = Math.max(0, irPelaTabela - reducao);
  return irrfFinal;
};

const calcularHoraAtividade = (salarioBase) => {
  return salarioBase * HORA_ATIVIDADE_PERCENT;
};

const calcularDSR = (salarioBase, horaAtividade, horaExtra) => {
  return (salarioBase + horaAtividade + horaExtra) / DSR_DIVISOR;
};

const calcularFGTS = (salarioBruto) => {
  return salarioBruto * FGTS_PERCENT;
};

const calcularFerias = (salarioBruto, diasFerias = 30) => {
  const valorFerias = (salarioBruto / 30) * diasFerias;
  const tercoConstitucional = valorFerias / 3;
  const totalBruto = valorFerias + tercoConstitucional;
  
  const inss = calcularINSS(totalBruto);
  const irrf = calcularIRRF(totalBruto, inss);
  const liquido = totalBruto - inss - irrf;
  
  return { valorFerias, tercoConstitucional, totalBruto, inss, irrf, liquido, diasFerias };
};

const calcularDecimoTerceiro = (salarioBruto, mesesTrabalhados = 12) => {
  const valorIntegral = (salarioBruto / 12) * mesesTrabalhados;
  const primeiraParcela = valorIntegral / 2;
  const segundaParcelaBruta = valorIntegral / 2;
  
  const inss = calcularINSS(valorIntegral);
  const irrf = calcularIRRF(valorIntegral, inss);
  
  const segundaParcelaLiquida = segundaParcelaBruta - inss - irrf;
  const totalLiquido = primeiraParcela + segundaParcelaLiquida;
  
  return { valorIntegral, primeiraParcela, segundaParcelaBruta, inss, irrf, segundaParcelaLiquida, totalLiquido, mesesTrabalhados };
};

// Calcula meses trabalhados no ano considerando a regra do dia 15
// Se admissão antes do dia 15, conta o mês inteiro
// Se admissão após dia 15, conta a partir do mês seguinte
const calcularMesesTrabalhados = (admissionDate, referenceYear = new Date().getFullYear()) => {
  if (!admissionDate) return 12; // Se não tem data, considera ano completo
  
  const admission = new Date(admissionDate);
  const admissionYear = admission.getFullYear();
  const admissionMonth = admission.getMonth(); // 0-11
  const admissionDay = admission.getDate();
  
  // Se admitido em ano posterior ao de referência, retorna 0
  if (admissionYear > referenceYear) return 0;
  
  // Se admitido em ano anterior ao de referência, conta 12 meses
  if (admissionYear < referenceYear) return 12;
  
  // Admitido no ano de referência
  // Mês de início: se admissão até dia 15, conta o mês; senão, próximo mês
  let startMonth = admissionMonth;
  if (admissionDay > 15) {
    startMonth = admissionMonth + 1;
  }
  
  // Meses de janeiro a dezembro = 12 - startMonth
  const meses = Math.max(0, 12 - startMonth);
  return meses;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================

export default function FolhaPagamento() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [payrollData, setPayrollData] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [totals, setTotals] = useState({
    salarioBruto: 0, inss: 0, irrf: 0, fgts: 0, descontos: 0, liquido: 0
  });
  const [activeTab, setActiveTab] = useState('folha');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [feriasCalc, setFeriasCalc] = useState(null);
  const [decimoCalc, setDecimoCalc] = useState(null);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showHoleriteDialog, setShowHoleriteDialog] = useState(false);
  const [holeriteData, setHoleriteData] = useState(null);
  const [newCompany, setNewCompany] = useState({ name: '', cnpj: '', address: '' });
  // Recibo Cesta Básica
  const [showCestaDialog, setShowCestaDialog] = useState(false);
  const [cestaData, setCestaData] = useState(null);
  const [selectedCestaMonths, setSelectedCestaMonths] = useState([]);
  const [selectedCestaYear, setSelectedCestaYear] = useState('2026');
  const [selectedCestaEmployee, setSelectedCestaEmployee] = useState(null);
  // Dialogs de impressão Férias e 13º
  const [showFeriasDialog, setShowFeriasDialog] = useState(false);
  const [showDecimoDialog, setShowDecimoDialog] = useState(false);

  const months = [
    { value: '2026-01', label: 'Janeiro/2026' },
    { value: '2026-02', label: 'Fevereiro/2026' },
    { value: '2026-03', label: 'Março/2026' },
    { value: '2026-04', label: 'Abril/2026' },
    { value: '2026-05', label: 'Maio/2026' },
    { value: '2026-06', label: 'Junho/2026' },
    { value: '2026-07', label: 'Julho/2026' },
    { value: '2026-08', label: 'Agosto/2026' },
    { value: '2026-09', label: 'Setembro/2026' },
    { value: '2026-10', label: 'Outubro/2026' },
    { value: '2026-11', label: 'Novembro/2026' },
    { value: '2026-12', label: 'Dezembro/2026' },
  ];

  useEffect(() => {
    loadCompanies();
    loadEmployees();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      loadPayroll();
    }
  }, [selectedMonth, employees, selectedCompany]);

  useEffect(() => {
    calculateTotals();
  }, [payrollData]);

  const loadCompanies = async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data || []);
    } catch (error) {
      // Empresa padrão se não houver cadastro
      setCompanies([{ id: 'default', name: 'Novo DNA Instituto de Aprendizagem', cnpj: '00.000.000/0001-00' }]);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await api.get('/employees');
      // Filtrar apenas funcionários com active === true
      const activeEmployees = (res.data || []).filter(emp => emp.active === true);
      setEmployees(activeEmployees);
    } catch (error) {
      toast.error('Erro ao carregar funcionários');
    }
  };

  const loadPayroll = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payroll?month=${selectedMonth}${selectedCompany !== 'all' ? `&company_id=${selectedCompany}` : ''}`);
      if (res.data && res.data.length > 0) {
        // Filtrar apenas funcionários ativos (que estão no array employees)
        const activeIds = new Set(employees.map(e => e.id));
        const activePayroll = res.data.filter(p => activeIds.has(p.employee_id));
        
        if (activePayroll.length > 0) {
          // Mesclar dados do payroll salvo com dados atualizados dos funcionários
          const employeeMap = new Map(employees.map(e => [e.id, e]));
          
          const mergedData = activePayroll.map(payrollItem => {
            const emp = employeeMap.get(payrollItem.employee_id);
            if (emp) {
              // Atualizar professor_type e contract_type do cadastro
              const contractType = emp.contract_type || 'clt';
              const professorType = emp.professor_type || '';
              const valorHoraCadastro = parseFloat(emp.hourly_rate) || 0;
              const salarioCadastro = parseFloat(emp.monthly_salary) || parseFloat(emp.salary) || 0;
              
              // Se o salário base não foi preenchido, usar do cadastro
              let salarioBase = parseFloat(payrollItem.salario_base) || 0;
              if (salarioBase === 0 && professorType !== 'horista' && salarioCadastro > 0) {
                salarioBase = salarioCadastro;
              }
              
              // Se valor hora não foi preenchido, usar do cadastro
              let horaAulaTotal = parseFloat(payrollItem.hora_aula_total) || 0;
              if (horaAulaTotal === 0 && professorType === 'horista' && valorHoraCadastro > 0) {
                horaAulaTotal = valorHoraCadastro;
              }
              
              const updatedItem = {
                ...payrollItem,
                contract_type: contractType,
                professor_type: professorType,
                cargo: emp.role || emp.title || payrollItem.cargo || '',
                valor_hora_cadastro: valorHoraCadastro,
                hora_aula_total: horaAulaTotal,
                salario_base: salarioBase
              };
              
              // Recalcular com os dados atualizados
              return recalcularFuncionario(updatedItem);
            }
            return payrollItem;
          });
          
          setPayrollData(mergedData);
        } else {
          initializePayrollData();
        }
      } else {
        initializePayrollData();
      }
    } catch (error) {
      initializePayrollData();
    } finally {
      setLoading(false);
    }
  };

  const initializePayrollData = () => {
    const filteredEmployees = selectedCompany !== 'all' 
      ? employees.filter(e => e.company_id === selectedCompany)
      : employees;
      
    const initialData = filteredEmployees.map(emp => {
      const contractType = emp.contract_type || 'clt';
      const professorType = emp.professor_type || '';
      
      // Puxar do cadastro: salário (mensalista/funcionário) ou valor hora (horista)
      const salarioCadastro = parseFloat(emp.monthly_salary) || parseFloat(emp.salary) || 0;
      const valorHoraCadastro = parseFloat(emp.hourly_rate) || 0;
      
      // Para horista: salário base será calculado quando informar nº aulas
      // Para mensalista/funcionário: usa salário do cadastro
      let salarioBase = professorType === 'horista' ? 0 : salarioCadastro;
      
      // Criar item inicial
      const initialItem = {
        employee_id: emp.id,
        employee_name: emp.full_name || emp.name,
        cargo: emp.role || emp.title || '',
        company_id: emp.company_id || 'default',
        contract_type: contractType,
        professor_type: professorType,
        admission_date: emp.admission_date || null,
        month: selectedMonth,
        // Proventos
        salario_base: salarioBase,
        num_aulas: 0,
        valor_hora_cadastro: valorHoraCadastro,
        hora_aula_total: valorHoraCadastro,
        hora_atividade: 0,
        dsr: 0,
        hora_extra: 0,
        adicional_1_tipo: 'nenhum',
        adicional_1: 0,
        adicional_2_tipo: 'nenhum',
        adicional_2: 0,
        retroativo: 0,
        // Descontos
        inss: 0,
        irrf: 0,
        adiantamento: 0,
        almoco: 0,
        material_didatico: 0,
        falta: 0,
        // Totais
        fgts: 0,
        salario_bruto: 0,
        total_descontos: 0,
        liquido: 0
      };
      
      // Recalcular para aplicar todas as regras
      return recalcularFuncionario(initialItem);
    });
    
    setPayrollData(initialData);
  };

  const calculateTotals = () => {
    const totals = payrollData.reduce((acc, item) => ({
      salarioBruto: acc.salarioBruto + (parseFloat(item.salario_bruto) || 0),
      inss: acc.inss + (parseFloat(item.inss) || 0),
      irrf: acc.irrf + (parseFloat(item.irrf) || 0),
      fgts: acc.fgts + (parseFloat(item.fgts) || 0),
      descontos: acc.descontos + (parseFloat(item.adiantamento) || 0) + (parseFloat(item.almoco) || 0) + 
                 (parseFloat(item.material_didatico) || 0) + (parseFloat(item.falta) || 0),
      liquido: acc.liquido + (parseFloat(item.liquido) || 0)
    }), { salarioBruto: 0, inss: 0, irrf: 0, fgts: 0, descontos: 0, liquido: 0 });
    setTotals(totals);
  };

  const recalcularFuncionario = (item) => {
    const contractType = item.contract_type || 'clt';
    const professorType = item.professor_type || '';
    
    // Valores informados
    const numAulas = parseFloat(item.num_aulas) || 0;
    const valorHoraAula = parseFloat(item.hora_aula_total) || 0;
    
    // Salário base:
    // - Horista: calcula por Nº Aulas × Vlr H/Aula × 4.5
    // - Mensalista/Funcionário: usa valor manual do cadastro ou input
    let salarioBase = 0;
    if (professorType === 'horista' && numAulas > 0 && valorHoraAula > 0) {
      salarioBase = numAulas * valorHoraAula * SEMANAS_MES;
    } else {
      salarioBase = parseFloat(item.salario_base) || 0;
    }
    
    let horaAtividade = 0;
    let dsr = 0;
    let salarioBruto = 0;
    let inss = 0;
    let irrf = 0;
    let fgts = 0;
    
    // Adicionais
    const adicional1 = parseFloat(item.adicional_1) || 0;
    const adicional2 = parseFloat(item.adicional_2) || 0;
    const retroativo = parseFloat(item.retroativo) || 0;
    const horaExtra = parseFloat(item.hora_extra) || 0;
    const totalAdicionais = adicional1 + adicional2 + retroativo;
    
    // PJ ou Contrato de Experiência: SEM encargos
    if (contractType === 'pj' || contractType === 'experiencia') {
      if (professorType === 'mensalista') {
        horaAtividade = salarioBase * HORA_ATIVIDADE_PERCENT;
        salarioBruto = salarioBase + horaAtividade + horaExtra + totalAdicionais;
      } else if (professorType === 'horista') {
        horaAtividade = salarioBase * HORA_ATIVIDADE_PERCENT;
        dsr = (salarioBase + horaAtividade + horaExtra) / DSR_DIVISOR;
        salarioBruto = salarioBase + horaAtividade + dsr + horaExtra + totalAdicionais;
      } else {
        salarioBruto = salarioBase + horaExtra + totalAdicionais;
      }
      // SEM INSS, IRRF, FGTS
      inss = 0;
      irrf = 0;
      fgts = 0;
    } else {
      // CLT: COM encargos
      if (professorType === 'mensalista') {
        horaAtividade = salarioBase * HORA_ATIVIDADE_PERCENT;
        salarioBruto = salarioBase + horaAtividade + horaExtra + totalAdicionais;
      } else if (professorType === 'horista') {
        horaAtividade = salarioBase * HORA_ATIVIDADE_PERCENT;
        dsr = (salarioBase + horaAtividade + horaExtra) / DSR_DIVISOR;
        salarioBruto = salarioBase + horaAtividade + dsr + horaExtra + totalAdicionais;
      } else {
        // Funcionário comum
        salarioBruto = salarioBase + horaExtra + totalAdicionais;
      }
      inss = calcularINSS(salarioBruto);
      irrf = calcularIRRF(salarioBruto, inss);
      fgts = calcularFGTS(salarioBruto);
    }
    
    // Total descontos
    const adiantamento = parseFloat(item.adiantamento) || 0;
    const almoco = parseFloat(item.almoco) || 0;
    const materialDidatico = parseFloat(item.material_didatico) || 0;
    const falta = parseFloat(item.falta) || 0;
    const totalDescontos = inss + irrf + adiantamento + almoco + materialDidatico + falta;
    
    // Líquido
    const liquido = salarioBruto - totalDescontos;
    
    return {
      ...item,
      salario_base: salarioBase,
      hora_atividade: horaAtividade,
      dsr: dsr,
      inss: inss,
      irrf: irrf,
      fgts: fgts,
      salario_bruto: salarioBruto,
      total_descontos: totalDescontos,
      liquido: liquido
    };
  };

  const updatePayrollItem = (index, field, value) => {
    const updated = [...payrollData];
    
    // Atualizar o campo
    if (field === 'nivel_ensino') {
      updated[index][field] = value;
      updated[index]['valor_hora_aula'] = HORA_AULA_SINPRO[value].valor;
    } else {
      updated[index][field] = parseFloat(value) || (typeof value === 'string' ? value : 0);
    }
    
    // Recalcular todos os valores
    updated[index] = recalcularFuncionario(updated[index]);
    
    setPayrollData(updated);
  };

  const savePayroll = async () => {
    setSaving(true);
    try {
      await api.post('/payroll', {
        month: selectedMonth,
        company_id: selectedCompany,
        data: payrollData
      });
      toast.success('Folha de pagamento salva!');
    } catch (error) {
      toast.error('Erro ao salvar folha de pagamento');
    } finally {
      setSaving(false);
    }
  };

  const saveCompany = async () => {
    try {
      await api.post('/companies', newCompany);
      toast.success('Empresa cadastrada!');
      loadCompanies();
      setShowCompanyDialog(false);
      setNewCompany({ name: '', cnpj: '', address: '' });
    } catch (error) {
      toast.error('Erro ao cadastrar empresa');
    }
  };

  const gerarHolerite = (employeeId) => {
    const emp = payrollData.find(p => p.employee_id === employeeId);
    if (emp) {
      const company = companies.find(c => c.id === emp.company_id) || companies[0] || { name: 'Novo DNA', cnpj: '00.000.000/0001-00' };
      setHoleriteData({ ...emp, company });
      setShowHoleriteDialog(true);
    }
  };

  const toggleCestaMonth = (month) => {
    setSelectedCestaMonths(prev => 
      prev.includes(month) 
        ? prev.filter(m => m !== month)
        : [...prev, month].sort()
    );
  };

  const getMonthLabel = (monthValue) => {
    const monthNames = {
      '01': 'Janeiro', '02': 'Fevereiro', '03': 'Março', '04': 'Abril',
      '05': 'Maio', '06': 'Junho', '07': 'Julho', '08': 'Agosto',
      '09': 'Setembro', '10': 'Outubro', '11': 'Novembro', '12': 'Dezembro'
    };
    const [year, month] = monthValue.split('-');
    return `${monthNames[month]}/${year}`;
  };

  const exportExcel = async () => {
    try {
      const response = await api.post('/payroll/export', {
        month: selectedMonth,
        company_id: selectedCompany,
        format: 'xlsx'
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `folha_pagamento_${selectedMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado!');
    } catch (error) {
      toast.error('Erro ao exportar Excel');
    }
  };

  const exportPDF = async () => {
    try {
      const response = await api.post('/payroll/export', {
        month: selectedMonth,
        company_id: selectedCompany,
        format: 'pdf'
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `folha_pagamento_${selectedMonth}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado!');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const calcularFeriasFunc = (empId, diasFerias = 30) => {
    const emp = payrollData.find(p => p.employee_id === empId);
    if (emp) {
      const calc = calcularFerias(emp.salario_bruto || emp.salario_base, diasFerias);
      setFeriasCalc({ ...calc, employee_name: emp.employee_name });
    }
  };

  const calcularDecimoFunc = (empId, meses = 12) => {
    const emp = payrollData.find(p => p.employee_id === empId);
    if (emp) {
      const calc = calcularDecimoTerceiro(emp.salario_bruto || emp.salario_base, meses);
      setDecimoCalc({ ...calc, employee_name: emp.employee_name });
    }
  };

  const monthLabel = months.find(m => m.value === selectedMonth)?.label || selectedMonth;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-green-600" />
            Folha de Pagamento
          </h1>
          <p className="text-muted-foreground">Convenção SINPRO/SP 2026</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-56">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Empresa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as Empresas</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="icon">
                <Plus className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Cadastrar Empresa</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Nome da Empresa</Label>
                  <Input 
                    value={newCompany.name} 
                    onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                    placeholder="Razão Social"
                  />
                </div>
                <div>
                  <Label>CNPJ</Label>
                  <Input 
                    value={newCompany.cnpj} 
                    onChange={(e) => setNewCompany({...newCompany, cnpj: e.target.value})}
                    placeholder="00.000.000/0001-00"
                  />
                </div>
                <div>
                  <Label>Endereço</Label>
                  <Input 
                    value={newCompany.address} 
                    onChange={(e) => setNewCompany({...newCompany, address: e.target.value})}
                    placeholder="Endereço completo"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveCompany}>Salvar Empresa</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button onClick={savePayroll} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar
          </Button>
          
          <Button variant="outline" onClick={exportExcel}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          
          <Button variant="outline" onClick={exportPDF}>
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Abas */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-lg">
          <TabsTrigger value="folha" className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            Folha Mensal
          </TabsTrigger>
          <TabsTrigger value="ferias" className="flex items-center gap-2">
            <Umbrella className="w-4 h-4" />
            Férias
          </TabsTrigger>
          <TabsTrigger value="decimo" className="flex items-center gap-2">
            <Gift className="w-4 h-4" />
            13º Salário
          </TabsTrigger>
          <TabsTrigger value="cesta" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Cesta Básica
          </TabsTrigger>
        </TabsList>

        {/* Aba Folha Mensal */}
        <TabsContent value="folha" className="space-y-4">
          {/* Resumo */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Total Bruto</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(totals.salarioBruto)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">INSS</p>
                <p className="text-xl font-bold text-red-500">{formatCurrency(totals.inss)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">IRRF</p>
                <p className="text-xl font-bold text-red-500">{formatCurrency(totals.irrf)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">FGTS (8%)</p>
                <p className="text-xl font-bold text-orange-500">{formatCurrency(totals.fgts)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Outros Descontos</p>
                <p className="text-xl font-bold text-orange-500">{formatCurrency(totals.descontos)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Total Líquido</p>
                <p className="text-xl font-bold text-blue-600">{formatCurrency(totals.liquido)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabela de Funcionários */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Funcionários ({payrollData.length}) - {monthLabel}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[150px] sticky left-0 bg-background z-10">Funcionário</TableHead>
                        <TableHead className="min-w-[100px]">Cargo</TableHead>
                        <TableHead className="text-center min-w-[70px]">Nº Aulas</TableHead>
                        <TableHead className="text-right min-w-[100px]">Vlr H/Aula</TableHead>
                        <TableHead className="text-right min-w-[100px]">Sal. Base</TableHead>
                        <TableHead className="text-right min-w-[90px]">H.Ativ(5%)</TableHead>
                        <TableHead className="text-right min-w-[90px]">DSR(1/6)</TableHead>
                        <TableHead className="text-right min-w-[80px]">H.Extra</TableHead>
                        <TableHead className="min-w-[150px]">Adicional 1</TableHead>
                        <TableHead className="text-right min-w-[80px]">Valor 1</TableHead>
                        <TableHead className="min-w-[150px]">Adicional 2</TableHead>
                        <TableHead className="text-right min-w-[80px]">Valor 2</TableHead>
                        <TableHead className="text-right min-w-[80px]">INSS</TableHead>
                        <TableHead className="text-right min-w-[80px]">IRRF</TableHead>
                        <TableHead className="text-right min-w-[90px]">Adiantam.</TableHead>
                        <TableHead className="text-right min-w-[80px]">Almoço</TableHead>
                        <TableHead className="text-right min-w-[80px]">Mat.Did.</TableHead>
                        <TableHead className="text-right min-w-[70px]">Falta</TableHead>
                        <TableHead className="text-right font-bold min-w-[100px]">Bruto</TableHead>
                        <TableHead className="text-right font-bold min-w-[100px]">Líquido</TableHead>
                        <TableHead className="text-center min-w-[80px] sticky right-0 bg-background z-10">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payrollData.map((item, index) => (
                        <TableRow key={item.employee_id}>
                          <TableCell className="font-medium sticky left-0 bg-background z-10">{item.employee_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{item.cargo || '-'}</TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={item.num_aulas || ''} 
                              onChange={(e) => updatePayrollItem(index, 'num_aulas', e.target.value)}
                              className="w-14 text-center"
                              placeholder="0"
                              disabled={item.professor_type !== 'horista'}
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={item.hora_aula_total || ''} 
                              onChange={(e) => updatePayrollItem(index, 'hora_aula_total', e.target.value)}
                              className="w-24 text-right"
                              placeholder="0,00"
                              disabled={item.professor_type !== 'horista'}
                            />
                          </TableCell>
                          <TableCell>
                            {item.professor_type === 'horista' ? (
                              <span className="text-right font-medium block">{formatCurrency(item.salario_base)}</span>
                            ) : (
                              <Input 
                                type="number" 
                                value={item.salario_base || ''} 
                                onChange={(e) => updatePayrollItem(index, 'salario_base', e.target.value)}
                                className="w-24 text-right"
                              />
                            )}
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {formatCurrency(item.hora_atividade)}
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {formatCurrency(item.dsr)}
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={item.hora_extra || ''} 
                              onChange={(e) => updatePayrollItem(index, 'hora_extra', e.target.value)}
                              className="w-16 text-right"
                            />
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={item.adicional_1_tipo || 'nenhum'} 
                              onValueChange={(v) => updatePayrollItem(index, 'adicional_1_tipo', v)}
                            >
                              <SelectTrigger className="w-36">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="nenhum">Nenhum</SelectItem>
                                <SelectItem value="retroativo_dissidio">Retroativo Dissídio</SelectItem>
                                <SelectItem value="diferenca_salario">Diferença de Salário</SelectItem>
                                <SelectItem value="outros">Outros</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={item.adicional_1 || ''} 
                              onChange={(e) => updatePayrollItem(index, 'adicional_1', e.target.value)}
                              className="w-16 text-right"
                              disabled={!item.adicional_1_tipo || item.adicional_1_tipo === 'nenhum'}
                            />
                          </TableCell>
                          <TableCell>
                            <Select 
                              value={item.adicional_2_tipo || 'nenhum'} 
                              onValueChange={(v) => updatePayrollItem(index, 'adicional_2_tipo', v)}
                            >
                              <SelectTrigger className="w-36">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="nenhum">Nenhum</SelectItem>
                                <SelectItem value="retroativo_dissidio">Retroativo Dissídio</SelectItem>
                                <SelectItem value="diferenca_salario">Diferença de Salário</SelectItem>
                                <SelectItem value="outros">Outros</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={item.adicional_2 || ''} 
                              onChange={(e) => updatePayrollItem(index, 'adicional_2', e.target.value)}
                              className="w-16 text-right"
                              disabled={!item.adicional_2_tipo || item.adicional_2_tipo === 'nenhum'}
                            />
                          </TableCell>
                          <TableCell className="text-right text-red-500">
                            {formatCurrency(item.inss)}
                          </TableCell>
                          <TableCell className="text-right text-red-500">
                            {formatCurrency(item.irrf)}
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={item.adiantamento || ''} 
                              onChange={(e) => updatePayrollItem(index, 'adiantamento', e.target.value)}
                              className="w-16 text-right"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={item.almoco || ''} 
                              onChange={(e) => updatePayrollItem(index, 'almoco', e.target.value)}
                              className="w-16 text-right"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={item.material_didatico || ''} 
                              onChange={(e) => updatePayrollItem(index, 'material_didatico', e.target.value)}
                              className="w-16 text-right"
                            />
                          </TableCell>
                          <TableCell>
                            <Input 
                              type="number" 
                              value={item.falta || ''} 
                              onChange={(e) => updatePayrollItem(index, 'falta', e.target.value)}
                              className="w-14 text-right"
                            />
                          </TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(item.salario_bruto)}
                          </TableCell>
                          <TableCell className="text-right font-bold text-blue-600">
                            {formatCurrency(item.liquido)}
                          </TableCell>
                          <TableCell className="text-center sticky right-0 bg-background z-10">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => gerarHolerite(item.employee_id)}
                              title="Gerar Holerite"
                              data-testid={`holerite-btn-${item.employee_id}`}
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tabelas de Referência */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  SINPRO/SP - Hora-Aula 2026
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nível de Ensino</TableHead>
                      <TableHead className="text-right">Valor H/A</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(HORA_AULA_SINPRO).map(([key, val]) => (
                      <TableRow key={key}>
                        <TableCell className="text-sm">{val.descricao}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(val.valor)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Tabela INSS 2026
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Faixa Salarial</TableHead>
                      <TableHead>Alíquota</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow><TableCell>Até R$ 1.621,00</TableCell><TableCell>7,5%</TableCell></TableRow>
                    <TableRow><TableCell>R$ 1.621,01 - R$ 2.902,84</TableCell><TableCell>9,0%</TableCell></TableRow>
                    <TableRow><TableCell>R$ 2.902,85 - R$ 4.354,27</TableCell><TableCell>12,0%</TableCell></TableRow>
                    <TableRow><TableCell>R$ 4.354,28 - R$ 8.475,55</TableCell><TableCell>14,0%</TableCell></TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Regras SINPRO/SP
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <p><strong>Hora-atividade:</strong> 5% do salário base</p>
                <p><strong>DSR:</strong> (Base + H.Ativ + H.Extra) ÷ 6</p>
                <p><strong>FGTS:</strong> 8% do salário bruto</p>
                <p><strong>Hora Extra:</strong> +50% sobre H/A</p>
                <p><strong>Adicional Noturno:</strong> +20% sobre H/A</p>
                <p><strong>Semanas/mês:</strong> 4,5</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Aba Férias */}
        <TabsContent value="ferias" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Umbrella className="w-5 h-5 text-blue-600" />
                Cálculo de Férias - Todos os Funcionários
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Regras de Férias (CLT):</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>Período aquisitivo:</strong> 12 meses de trabalho para ter direito a férias</li>
                  <li>• <strong>Regra do dia 15:</strong> Admissão até dia 15 = conta o mês inteiro</li>
                  <li>• <strong>Valor das Férias</strong> = (Salário Bruto ÷ 30) × Dias de férias</li>
                  <li>• <strong>1/3 Constitucional</strong> = Valor das Férias ÷ 3</li>
                  <li>• Pagamento deve ser feito até 2 dias antes do início das férias</li>
                </ul>
              </div>

              <div className="overflow-x-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[180px]">Funcionário</TableHead>
                      <TableHead>Cargo</TableHead>
                      <TableHead>Admissão</TableHead>
                      <TableHead className="text-right">Sal. Base</TableHead>
                      <TableHead className="text-right">Valor Férias</TableHead>
                      <TableHead className="text-right">1/3 Const.</TableHead>
                      <TableHead className="text-right">Total Bruto</TableHead>
                      <TableHead className="text-right">INSS</TableHead>
                      <TableHead className="text-right">IRRF</TableHead>
                      <TableHead className="text-right font-bold">Líquido</TableHead>
                      <TableHead className="text-center">Imprimir</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payrollData.map((emp) => {
                      const calc = calcularFerias(emp.salario_bruto || emp.salario_base, 30);
                      return (
                        <TableRow key={emp.employee_id}>
                          <TableCell className="font-medium">{emp.employee_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{emp.cargo || '-'}</TableCell>
                          <TableCell className="text-sm">{formatDate(emp.admission_date)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(emp.salario_bruto || emp.salario_base)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(calc.valorFerias)}</TableCell>
                          <TableCell className="text-right text-green-600">{formatCurrency(calc.tercoConstitucional)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(calc.totalBruto)}</TableCell>
                          <TableCell className="text-right text-red-500">- {formatCurrency(calc.inss)}</TableCell>
                          <TableCell className="text-right text-red-500">- {formatCurrency(calc.irrf)}</TableCell>
                          <TableCell className="text-right font-bold text-blue-600">{formatCurrency(calc.liquido)}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setFeriasCalc({ ...calc, employee_name: emp.employee_name, salario_base: emp.salario_bruto || emp.salario_base, admission_date: emp.admission_date });
                                setShowFeriasDialog(true);
                              }}
                              title="Imprimir Recibo de Férias"
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba 13º Salário */}
        <TabsContent value="decimo" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-600" />
                Cálculo do 13º Salário - Todos os Funcionários
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">Regras do 13º Salário:</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• <strong>Regra do dia 15:</strong> Admissão até dia 15 = conta o mês inteiro para o 13º</li>
                  <li>• <strong>Valor Integral</strong> = (Salário Bruto ÷ 12) × Meses trabalhados no ano</li>
                  <li>• <strong>1ª Parcela</strong> (até 30/nov) = 50% do valor integral <span className="text-green-600">(sem descontos)</span></li>
                  <li>• <strong>2ª Parcela</strong> (até 20/dez) = 50% do valor integral <span className="text-red-500">(com INSS e IRRF)</span></li>
                </ul>
              </div>

              <div className="overflow-x-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[180px]">Funcionário</TableHead>
                      <TableHead>Cargo</TableHead>
                      <TableHead>Admissão</TableHead>
                      <TableHead className="text-center">Meses</TableHead>
                      <TableHead className="text-right">Sal. Base</TableHead>
                      <TableHead className="text-right">13º Integral</TableHead>
                      <TableHead className="text-right">1ª Parcela</TableHead>
                      <TableHead className="text-right">INSS</TableHead>
                      <TableHead className="text-right">IRRF</TableHead>
                      <TableHead className="text-right">2ª Parcela Líq.</TableHead>
                      <TableHead className="text-right font-bold">Total Líquido</TableHead>
                      <TableHead className="text-center">Imprimir</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payrollData.map((emp) => {
                      const mesesTrabalhados = calcularMesesTrabalhados(emp.admission_date, parseInt(selectedMonth.split('-')[0]));
                      const calc = calcularDecimoTerceiro(emp.salario_bruto || emp.salario_base, mesesTrabalhados);
                      return (
                        <TableRow key={emp.employee_id}>
                          <TableCell className="font-medium">{emp.employee_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{emp.cargo || '-'}</TableCell>
                          <TableCell className="text-sm">{formatDate(emp.admission_date)}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-bold ${mesesTrabalhados === 12 ? 'text-green-600' : 'text-orange-500'}`}>
                              {mesesTrabalhados}/12
                            </span>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(emp.salario_bruto || emp.salario_base)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(calc.valorIntegral)}</TableCell>
                          <TableCell className="text-right text-green-600">{formatCurrency(calc.primeiraParcela)}</TableCell>
                          <TableCell className="text-right text-red-500">- {formatCurrency(calc.inss)}</TableCell>
                          <TableCell className="text-right text-red-500">- {formatCurrency(calc.irrf)}</TableCell>
                          <TableCell className="text-right text-purple-600">{formatCurrency(calc.segundaParcelaLiquida)}</TableCell>
                          <TableCell className="text-right font-bold text-purple-600">{formatCurrency(calc.totalLiquido)}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setDecimoCalc({ ...calc, employee_name: emp.employee_name, salario_base: emp.salario_bruto || emp.salario_base, admission_date: emp.admission_date });
                                setShowDecimoDialog(true);
                              }}
                              title="Imprimir Recibo de 13º"
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Totais */}
              <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total 13º Integral</p>
                      <p className="text-xl font-bold">
                        {formatCurrency(payrollData.reduce((acc, emp) => {
                          const meses = calcularMesesTrabalhados(emp.admission_date, parseInt(selectedMonth.split('-')[0]));
                          const calc = calcularDecimoTerceiro(emp.salario_bruto || emp.salario_base, meses);
                          return acc + calc.valorIntegral;
                        }, 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total 1ª Parcela</p>
                      <p className="text-xl font-bold text-green-600">
                        {formatCurrency(payrollData.reduce((acc, emp) => {
                          const meses = calcularMesesTrabalhados(emp.admission_date, parseInt(selectedMonth.split('-')[0]));
                          const calc = calcularDecimoTerceiro(emp.salario_bruto || emp.salario_base, meses);
                          return acc + calc.primeiraParcela;
                        }, 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total 2ª Parcela Líq.</p>
                      <p className="text-xl font-bold text-purple-600">
                        {formatCurrency(payrollData.reduce((acc, emp) => {
                          const meses = calcularMesesTrabalhados(emp.admission_date, parseInt(selectedMonth.split('-')[0]));
                          const calc = calcularDecimoTerceiro(emp.salario_bruto || emp.salario_base, meses);
                          return acc + calc.segundaParcelaLiquida;
                        }, 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Líquido Geral</p>
                      <p className="text-xl font-bold text-purple-700">
                        {formatCurrency(payrollData.reduce((acc, emp) => {
                          const meses = calcularMesesTrabalhados(emp.admission_date, parseInt(selectedMonth.split('-')[0]));
                          const calc = calcularDecimoTerceiro(emp.salario_bruto || emp.salario_base, meses);
                          return acc + calc.totalLiquido;
                        }, 0))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Cesta Básica */}
        <TabsContent value="cesta" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-600" />
                Recibo de Entrega de Cesta Básica
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-900 mb-2">Como gerar o recibo:</h4>
                <ul className="text-sm text-orange-800 space-y-1">
                  <li>• Selecione o funcionário</li>
                  <li>• Escolha o ano de referência</li>
                  <li>• Marque os meses de entrega da cesta básica</li>
                  <li>• Clique em "Gerar Recibo" para visualizar e imprimir</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Funcionário</Label>
                  <Select onValueChange={(v) => {
                    setSelectedCestaEmployee(v);
                    const emp = payrollData.find(p => p.employee_id === v);
                    if (emp) {
                      const company = companies.find(c => c.id === emp.company_id) || companies[0] || { name: 'Novo DNA Instituto de Aprendizagem', cnpj: '00.000.000/0001-00' };
                      setCestaData({ ...emp, company });
                    }
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um funcionário" />
                    </SelectTrigger>
                    <SelectContent>
                      {payrollData.map(emp => (
                        <SelectItem key={emp.employee_id} value={emp.employee_id}>
                          {emp.employee_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Ano de Referência</Label>
                  <Select value={selectedCestaYear} onValueChange={setSelectedCestaYear}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2024">2024</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                      <SelectItem value="2027">2027</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {selectedCestaEmployee && (
                <>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Meses de Entrega - {selectedCestaYear}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                        {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(m => {
                          const monthValue = `${selectedCestaYear}-${m}`;
                          const monthNames = {
                            '01': 'Janeiro', '02': 'Fevereiro', '03': 'Março', '04': 'Abril',
                            '05': 'Maio', '06': 'Junho', '07': 'Julho', '08': 'Agosto',
                            '09': 'Setembro', '10': 'Outubro', '11': 'Novembro', '12': 'Dezembro'
                          };
                          return (
                            <div key={monthValue} className="flex items-center space-x-2">
                              <Checkbox
                                id={`cesta-${monthValue}`}
                                checked={selectedCestaMonths.includes(monthValue)}
                                onCheckedChange={() => toggleCestaMonth(monthValue)}
                              />
                              <Label htmlFor={`cesta-${monthValue}`} className="text-sm cursor-pointer">
                                {monthNames[m]}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            const allMonths = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(m => `${selectedCestaYear}-${m}`);
                            setSelectedCestaMonths(allMonths);
                          }}
                        >
                          Selecionar Todos
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedCestaMonths([])}
                        >
                          Limpar Seleção
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {selectedCestaMonths.length > 0 && (
                    <Button 
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      onClick={() => setShowCestaDialog(true)}
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Gerar Recibo ({selectedCestaMonths.length} {selectedCestaMonths.length === 1 ? 'mês' : 'meses'})
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Holerite */}
      <Dialog open={showHoleriteDialog} onOpenChange={setShowHoleriteDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Holerite - {holeriteData?.employee_name}
            </DialogTitle>
          </DialogHeader>
          
          {holeriteData && (
            <div className="border rounded-lg p-4 space-y-4 bg-white" id="holerite-content">
              {/* Cabeçalho Empresa */}
              <div className="border-b pb-4">
                <h2 className="font-bold text-lg">{holeriteData.company?.name || 'Novo DNA Instituto de Aprendizagem'}</h2>
                <p className="text-sm text-muted-foreground">CNPJ: {holeriteData.company?.cnpj || '00.000.000/0001-00'}</p>
                <p className="text-sm text-muted-foreground">{holeriteData.company?.address || ''}</p>
              </div>

              {/* Info Funcionário */}
              <div className="grid grid-cols-2 gap-4 border-b pb-4">
                <div>
                  <p className="text-sm text-muted-foreground">Funcionário</p>
                  <p className="font-medium">{holeriteData.employee_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cargo</p>
                  <p className="font-medium">{holeriteData.cargo || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Competência</p>
                  <p className="font-medium">{monthLabel}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tipo</p>
                  <p className="font-medium">
                    {holeriteData.contract_type === 'pj' ? 'PJ' : 
                     holeriteData.contract_type === 'experiencia' ? 'Experiência' : 'CLT'}
                    {holeriteData.professor_type === 'mensalista' && ' - Mensalista'}
                    {holeriteData.professor_type === 'horista' && ' - Horista'}
                  </p>
                </div>
              </div>

              {/* Proventos e Descontos */}
              <div className="grid grid-cols-2 gap-8">
                {/* Proventos */}
                <div>
                  <h3 className="font-bold text-green-700 border-b pb-2 mb-2">PROVENTOS</h3>
                  <div className="space-y-1 text-sm">
                    {/* Salário Base - sempre mostra */}
                    <div className="flex justify-between">
                      <span>Salário Base</span>
                      <span>{formatCurrency(holeriteData.salario_base)}</span>
                    </div>
                    
                    {/* Para Horista: mostra detalhes do cálculo */}
                    {holeriteData.professor_type === 'horista' && holeriteData.num_aulas > 0 && (
                      <div className="flex justify-between text-xs text-muted-foreground pl-2">
                        <span>({holeriteData.num_aulas} aulas × R$ {(holeriteData.hora_aula_total || 0).toFixed(2)} × 4,5 sem.)</span>
                        <span></span>
                      </div>
                    )}
                    
                    {/* Hora-Atividade para Mensalistas e Horistas */}
                    {holeriteData.hora_atividade > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Hora-Atividade (5%)</span>
                        <span>{formatCurrency(holeriteData.hora_atividade)}</span>
                      </div>
                    )}
                    
                    {/* DSR apenas para Horistas */}
                    {holeriteData.dsr > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>DSR (1/6)</span>
                        <span>{formatCurrency(holeriteData.dsr)}</span>
                      </div>
                    )}
                    
                    {holeriteData.hora_extra > 0 && (
                      <div className="flex justify-between">
                        <span>Hora Extra</span>
                        <span>{formatCurrency(holeriteData.hora_extra)}</span>
                      </div>
                    )}
                    
                    {holeriteData.adicional_1 > 0 && holeriteData.adicional_1_tipo && holeriteData.adicional_1_tipo !== 'nenhum' && (
                      <div className="flex justify-between">
                        <span>{
                          holeriteData.adicional_1_tipo === 'retroativo_dissidio' ? 'Retroativo Dissídio' :
                          holeriteData.adicional_1_tipo === 'diferenca_salario' ? 'Diferença de Salário' :
                          'Outros'
                        }</span>
                        <span>{formatCurrency(holeriteData.adicional_1)}</span>
                      </div>
                    )}
                    
                    {holeriteData.adicional_2 > 0 && holeriteData.adicional_2_tipo && holeriteData.adicional_2_tipo !== 'nenhum' && (
                      <div className="flex justify-between">
                        <span>{
                          holeriteData.adicional_2_tipo === 'retroativo_dissidio' ? 'Retroativo Dissídio' :
                          holeriteData.adicional_2_tipo === 'diferenca_salario' ? 'Diferença de Salário' :
                          'Outros'
                        }</span>
                        <span>{formatCurrency(holeriteData.adicional_2)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between font-bold border-t pt-2 mt-2">
                      <span>TOTAL PROVENTOS</span>
                      <span className="text-green-700">{formatCurrency(holeriteData.salario_bruto)}</span>
                    </div>
                  </div>
                </div>

                {/* Descontos */}
                <div>
                  <h3 className="font-bold text-red-700 border-b pb-2 mb-2">DESCONTOS</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>INSS</span>
                      <span>{formatCurrency(holeriteData.inss)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>IRRF</span>
                      <span>{formatCurrency(holeriteData.irrf)}</span>
                    </div>
                    {holeriteData.adiantamento > 0 && (
                      <div className="flex justify-between">
                        <span>Adiantamento</span>
                        <span>{formatCurrency(holeriteData.adiantamento)}</span>
                      </div>
                    )}
                    {holeriteData.almoco > 0 && (
                      <div className="flex justify-between">
                        <span>Almoço</span>
                        <span>{formatCurrency(holeriteData.almoco)}</span>
                      </div>
                    )}
                    {holeriteData.material_didatico > 0 && (
                      <div className="flex justify-between">
                        <span>Material Didático</span>
                        <span>{formatCurrency(holeriteData.material_didatico)}</span>
                      </div>
                    )}
                    {holeriteData.falta > 0 && (
                      <div className="flex justify-between">
                        <span>Faltas</span>
                        <span>{formatCurrency(holeriteData.falta)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold border-t pt-2 mt-2">
                      <span>TOTAL DESCONTOS</span>
                      <span className="text-red-700">{formatCurrency(holeriteData.total_descontos)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Totais */}
              <div className="border-t pt-4 mt-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <p className="text-sm text-muted-foreground">Base INSS</p>
                    <p className="font-bold">{formatCurrency(holeriteData.salario_bruto)}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <p className="text-sm text-muted-foreground">Base IRRF</p>
                    <p className="font-bold">{formatCurrency(holeriteData.salario_bruto - holeriteData.inss)}</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded">
                    <p className="text-sm text-muted-foreground">FGTS (8%)</p>
                    <p className="font-bold">{formatCurrency(holeriteData.fgts)}</p>
                  </div>
                </div>
                
                <div className="text-center p-4 bg-blue-50 rounded mt-4">
                  <p className="text-sm text-muted-foreground">VALOR LÍQUIDO</p>
                  <p className="text-3xl font-bold text-blue-700">{formatCurrency(holeriteData.liquido)}</p>
                </div>
              </div>

              {/* Assinatura */}
              <div className="grid grid-cols-2 gap-8 pt-8 mt-8 border-t">
                <div className="text-center">
                  <div className="border-t border-black pt-2">
                    <p className="text-sm">Empregador</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="border-t border-black pt-2">
                    <p className="text-sm">Funcionário</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHoleriteDialog(false)}>
              Fechar
            </Button>
            <Button onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Recibo Cesta Básica */}
      <Dialog open={showCestaDialog} onOpenChange={setShowCestaDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Recibo de Entrega - Cesta Básica
            </DialogTitle>
          </DialogHeader>
          
          {cestaData && (
            <div className="border rounded-lg p-6 bg-white print:p-4" id="recibo-cesta">
              {/* Cabeçalho */}
              <div className="text-center border-b pb-4 mb-4">
                <h2 className="font-bold text-xl">{cestaData.company?.name || 'NOVO DNA INSTITUTO DE APRENDIZAGEM'}</h2>
                <p className="text-sm text-muted-foreground">CNPJ: {cestaData.company?.cnpj || '00.000.000/0001-00'}</p>
              </div>

              <h3 className="text-center font-bold text-lg mb-6">RECIBO DE ENTREGA DE CESTA BÁSICA</h3>

              {/* Corpo do Recibo */}
              <div className="space-y-4">
                <p className="text-justify leading-relaxed">
                  Declaro para os devidos fins que recebi da empresa <strong>{cestaData.company?.name || 'NOVO DNA INSTITUTO DE APRENDIZAGEM'}</strong>, 
                  CNPJ: {cestaData.company?.cnpj || '00.000.000/0001-00'}, a(s) cesta(s) básica(s) referente(s) ao(s) mês(es) de:
                </p>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-bold text-center text-lg">
                    {selectedCestaMonths.length > 0 
                      ? selectedCestaMonths.map(m => getMonthLabel(m)).join(', ')
                      : 'Nenhum mês selecionado'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Funcionário</p>
                    <p className="font-bold">{cestaData.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Quantidade</p>
                    <p className="font-bold">{selectedCestaMonths.length} cesta(s) básica(s)</p>
                  </div>
                </div>

                <p className="text-justify leading-relaxed mt-4">
                  Por ser expressão da verdade, firmo o presente recibo para que produza os efeitos legais e de direito.
                </p>
              </div>

              {/* Local e Data */}
              <div className="text-center mt-8 mb-8">
                <p>São Paulo, _____ de _________________ de {selectedCestaYear}.</p>
              </div>

              {/* Assinatura */}
              <div className="mt-12 pt-8">
                <div className="border-t border-black mx-auto w-80 text-center pt-2">
                  <p className="font-bold">{cestaData.employee_name}</p>
                  <p className="text-sm text-muted-foreground">Assinatura do Funcionário</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCestaDialog(false)}>
              Fechar
            </Button>
            <Button onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Recibo de Férias */}
      <Dialog open={showFeriasDialog} onOpenChange={setShowFeriasDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Umbrella className="w-5 h-5" />
              Recibo de Férias - {feriasCalc?.employee_name}
            </DialogTitle>
          </DialogHeader>
          
          {feriasCalc && (
            <div className="border rounded-lg p-6 bg-white print:p-4" id="recibo-ferias">
              {/* Cabeçalho */}
              <div className="text-center border-b pb-4 mb-4">
                <h2 className="font-bold text-xl">NOVO DNA INSTITUTO DE APRENDIZAGEM</h2>
                <p className="text-sm text-muted-foreground">RECIBO DE FÉRIAS</p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Funcionário</p>
                    <p className="font-bold">{feriasCalc.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Data de Admissão</p>
                    <p className="font-bold">{formatDate(feriasCalc.admission_date)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Dias de Férias</p>
                    <p className="font-bold">{feriasCalc.diasFerias} dias</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Salário Base</p>
                    <p className="font-bold">{formatCurrency(feriasCalc.salario_base)}</p>
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-bold text-green-700 mb-2">PROVENTOS</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Valor das Férias ({feriasCalc.diasFerias} dias)</span>
                      <span>{formatCurrency(feriasCalc.valorFerias)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>1/3 Constitucional</span>
                      <span>{formatCurrency(feriasCalc.tercoConstitucional)}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t pt-2 mt-2">
                      <span>TOTAL BRUTO</span>
                      <span className="text-green-700">{formatCurrency(feriasCalc.totalBruto)}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-bold text-red-700 mb-2">DESCONTOS</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>INSS</span>
                      <span>{formatCurrency(feriasCalc.inss)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>IRRF</span>
                      <span>{formatCurrency(feriasCalc.irrf)}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t pt-2 mt-2">
                      <span>TOTAL DESCONTOS</span>
                      <span className="text-red-700">{formatCurrency(feriasCalc.inss + feriasCalc.irrf)}</span>
                    </div>
                  </div>
                </div>

                <div className="text-center p-4 bg-blue-50 rounded mt-4">
                  <p className="text-sm text-muted-foreground">VALOR LÍQUIDO A RECEBER</p>
                  <p className="text-3xl font-bold text-blue-700">{formatCurrency(feriasCalc.liquido)}</p>
                </div>

                {/* Assinatura */}
                <div className="grid grid-cols-2 gap-8 pt-8 mt-8 border-t">
                  <div className="text-center">
                    <div className="border-t border-black pt-2">
                      <p className="text-sm">Empregador</p>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="border-t border-black pt-2">
                      <p className="text-sm">Funcionário</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeriasDialog(false)}>
              Fechar
            </Button>
            <Button onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Recibo de 13º Salário */}
      <Dialog open={showDecimoDialog} onOpenChange={setShowDecimoDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="w-5 h-5" />
              Recibo de 13º Salário - {decimoCalc?.employee_name}
            </DialogTitle>
          </DialogHeader>
          
          {decimoCalc && (
            <div className="border rounded-lg p-6 bg-white print:p-4" id="recibo-decimo">
              {/* Cabeçalho */}
              <div className="text-center border-b pb-4 mb-4">
                <h2 className="font-bold text-xl">NOVO DNA INSTITUTO DE APRENDIZAGEM</h2>
                <p className="text-sm text-muted-foreground">RECIBO DE 13º SALÁRIO</p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Funcionário</p>
                    <p className="font-bold">{decimoCalc.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Data de Admissão</p>
                    <p className="font-bold">{formatDate(decimoCalc.admission_date)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Meses Trabalhados</p>
                    <p className="font-bold">{decimoCalc.mesesTrabalhados}/12 meses</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Salário Base</p>
                    <p className="font-bold">{formatCurrency(decimoCalc.salario_base)}</p>
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-bold text-green-700 mb-2">VALORES</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>13º Salário Integral ({decimoCalc.mesesTrabalhados}/12)</span>
                      <span>{formatCurrency(decimoCalc.valorIntegral)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>1ª Parcela (até 30/nov) - Sem descontos</span>
                      <span>{formatCurrency(decimoCalc.primeiraParcela)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>2ª Parcela Bruta (até 20/dez)</span>
                      <span>{formatCurrency(decimoCalc.segundaParcelaBruta)}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-bold text-red-700 mb-2">DESCONTOS (sobre 2ª parcela)</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>INSS (sobre integral)</span>
                      <span>{formatCurrency(decimoCalc.inss)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>IRRF (sobre integral)</span>
                      <span>{formatCurrency(decimoCalc.irrf)}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t pt-2 mt-2">
                      <span>TOTAL DESCONTOS</span>
                      <span className="text-red-700">{formatCurrency(decimoCalc.inss + decimoCalc.irrf)}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between text-purple-600">
                    <span className="font-bold">2ª Parcela Líquida</span>
                    <span className="font-bold">{formatCurrency(decimoCalc.segundaParcelaLiquida)}</span>
                  </div>
                </div>

                <div className="text-center p-4 bg-purple-50 rounded mt-4">
                  <p className="text-sm text-muted-foreground">TOTAL LÍQUIDO DO 13º SALÁRIO</p>
                  <p className="text-3xl font-bold text-purple-700">{formatCurrency(decimoCalc.totalLiquido)}</p>
                  <p className="text-xs text-muted-foreground mt-1">(1ª Parcela + 2ª Parcela Líquida)</p>
                </div>

                {/* Assinatura */}
                <div className="grid grid-cols-2 gap-8 pt-8 mt-8 border-t">
                  <div className="text-center">
                    <div className="border-t border-black pt-2">
                      <p className="text-sm">Empregador</p>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="border-t border-black pt-2">
                      <p className="text-sm">Funcionário</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecimoDialog(false)}>
              Fechar
            </Button>
            <Button onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
